import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  Child,
  Discipline,
  Level,
  Challenge,
  ChallengeResult,
  ChildProgress,
  ChildStats,
  DisciplineSlug,
} from "@jumpbean/shared";
import { apiPost, apiGet } from "../lib/api";

// ==================== Types ====================

/**
 * Progress data for a single challenge in the current session
 */
export interface ChallengeProgressEntry {
  challengeId: string;
  starsEarned: 1 | 2 | 3;
  attempts: number;
  timeSpentSeconds: number;
  completedAt: string;
}

/**
 * Session statistics tracked during gameplay
 */
export interface SessionStats {
  /** When the session started */
  sessionStartedAt: string;
  /** Total time in session (seconds) */
  timeInSession: number;
  /** Number of challenges completed this session */
  challengesCompleted: number;
  /** Stars earned this session */
  starsEarned: number;
}

/**
 * Badge earned data for display in modal
 */
export interface EarnedBadge {
  id: string;
  name: string;
  imageUrl: string;
  levelId: string;
  earnedAt: string;
}

/**
 * API response when recording progress
 */
export interface RecordProgressResponse {
  progress: {
    id: string;
    completedAt: string;
    starsEarned: number;
  };
  badgeEarned: EarnedBadge | null;
  levelComplete: boolean;
}

/**
 * Represents the current game navigation state
 */
export type GameScreen =
  | "child-select"
  | "discipline-select"
  | "level-select"
  | "challenge"
  | "level-complete"
  | "badge-earned";

/**
 * The complete game state interface
 */
export interface GameState {
  // ==================== Current Session ====================
  /** Currently selected child */
  currentChild: Child | null;
  /** Current discipline being played */
  currentDiscipline: Discipline | null;
  /** Current level being played */
  currentLevel: Level | null;
  /** Current challenge being played */
  currentChallenge: Challenge | null;
  /** Index of current challenge in the level (0-based) */
  currentChallengeIndex: number;
  /** All challenges for the current level */
  levelChallenges: Challenge[];

  // ==================== Progress ====================
  /** Progress for challenges completed in the current session */
  sessionProgress: ChallengeProgressEntry[];
  /** Cached progress from API (for display purposes) */
  cachedProgress: Map<string, ChildProgress>;
  /** Session statistics */
  sessionStats: SessionStats;
  /** Child's overall stats (synced from API) */
  childStats: ChildStats | null;

  // ==================== UI State ====================
  /** Whether data is loading */
  isLoading: boolean;
  /** Current loading message */
  loadingMessage: string;
  /** Whether to show badge modal */
  showBadgeModal: boolean;
  /** Badge earned to display in modal */
  earnedBadge: EarnedBadge | null;
  /** Whether level is complete (show celebration) */
  isLevelComplete: boolean;
  /** Current screen in game flow */
  currentScreen: GameScreen;
  /** Error message if any */
  error: string | null;
  /** Whether syncing with API */
  isSyncing: boolean;

  // ==================== Actions ====================

  // Child Management
  setCurrentChild: (child: Child | null) => void;
  clearChild: () => void;

  // Level/Discipline Navigation
  startDiscipline: (discipline: Discipline) => void;
  startLevel: (level: Level, challenges: Challenge[]) => void;
  exitLevel: () => void;
  exitDiscipline: () => void;

  // Challenge Flow
  startChallenge: (challenge: Challenge, index: number) => void;
  completeChallenge: (result: ChallengeResult) => Promise<void>;
  nextChallenge: () => boolean;
  skipChallenge: () => void;

  // Session Management
  startSession: () => void;
  endSession: () => void;
  updateSessionTime: () => void;

  // UI State
  setLoading: (loading: boolean, message?: string) => void;
  setError: (error: string | null) => void;
  showBadge: (badge: EarnedBadge) => void;
  hideBadgeModal: () => void;
  setLevelComplete: (complete: boolean) => void;
  setScreen: (screen: GameScreen) => void;

  // API Sync
  syncProgress: (childId: string, result: ChallengeResult) => Promise<RecordProgressResponse | null>;
  loadProgress: (childId: string) => Promise<void>;
  loadChildStats: (childId: string) => Promise<void>;
  setCachedProgress: (progress: ChildProgress[]) => void;
  setChildStats: (stats: ChildStats) => void;

  // Helpers
  getProgressForChallenge: (challengeId: string) => ChildProgress | ChallengeProgressEntry | null;
  isChallengeDone: (challengeId: string) => boolean;
  getLevelProgress: () => { completed: number; total: number; stars: number };
  reset: () => void;
}

// ==================== Initial State ====================

const initialSessionStats: SessionStats = {
  sessionStartedAt: new Date().toISOString(),
  timeInSession: 0,
  challengesCompleted: 0,
  starsEarned: 0,
};

const initialState = {
  // Session
  currentChild: null,
  currentDiscipline: null,
  currentLevel: null,
  currentChallenge: null,
  currentChallengeIndex: 0,
  levelChallenges: [],

  // Progress
  sessionProgress: [],
  cachedProgress: new Map<string, ChildProgress>(),
  sessionStats: { ...initialSessionStats },
  childStats: null,

  // UI
  isLoading: false,
  loadingMessage: "",
  showBadgeModal: false,
  earnedBadge: null,
  isLevelComplete: false,
  currentScreen: "child-select" as GameScreen,
  error: null,
  isSyncing: false,
};

// ==================== Store ====================

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ==================== Child Management ====================

      setCurrentChild: (child) => {
        set({
          currentChild: child,
          currentScreen: child ? "discipline-select" : "child-select",
          // Reset session when child changes
          sessionProgress: [],
          sessionStats: { ...initialSessionStats, sessionStartedAt: new Date().toISOString() },
          cachedProgress: new Map(),
          childStats: null,
        });
      },

      clearChild: () => {
        set({
          ...initialState,
          currentScreen: "child-select",
        });
      },

      // ==================== Level/Discipline Navigation ====================

      startDiscipline: (discipline) => {
        set({
          currentDiscipline: discipline,
          currentScreen: "level-select",
          currentLevel: null,
          currentChallenge: null,
          levelChallenges: [],
        });
      },

      startLevel: (level, challenges) => {
        // Find the first uncompleted challenge, or start from beginning
        const state = get();
        let startIndex = 0;

        for (let i = 0; i < challenges.length; i++) {
          const challenge = challenges[i];
          if (challenge && !state.isChallengeDone(challenge.id)) {
            startIndex = i;
            break;
          }
          // If all are done, start from beginning
          if (i === challenges.length - 1) {
            startIndex = 0;
          }
        }

        const firstChallenge = challenges[startIndex] || null;

        set({
          currentLevel: level,
          levelChallenges: challenges,
          currentChallengeIndex: startIndex,
          currentChallenge: firstChallenge,
          currentScreen: "challenge",
          isLevelComplete: false,
        });
      },

      exitLevel: () => {
        set({
          currentLevel: null,
          currentChallenge: null,
          levelChallenges: [],
          currentChallengeIndex: 0,
          currentScreen: "level-select",
          isLevelComplete: false,
        });
      },

      exitDiscipline: () => {
        set({
          currentDiscipline: null,
          currentLevel: null,
          currentChallenge: null,
          levelChallenges: [],
          currentChallengeIndex: 0,
          currentScreen: "discipline-select",
          isLevelComplete: false,
        });
      },

      // ==================== Challenge Flow ====================

      startChallenge: (challenge, index) => {
        set({
          currentChallenge: challenge,
          currentChallengeIndex: index,
          currentScreen: "challenge",
        });
      },

      completeChallenge: async (result) => {
        const state = get();
        const { currentChild, sessionProgress, sessionStats } = state;

        if (!currentChild) {
          console.error("No child selected");
          return;
        }

        // Create progress entry
        const progressEntry: ChallengeProgressEntry = {
          challengeId: result.challengeId,
          starsEarned: result.starsEarned,
          attempts: result.attempts,
          timeSpentSeconds: result.timeSpentSeconds,
          completedAt: new Date().toISOString(),
        };

        // Update session progress
        const existingIndex = sessionProgress.findIndex(
          (p) => p.challengeId === result.challengeId
        );
        const newProgress = [...sessionProgress];
        if (existingIndex >= 0) {
          // Update existing (keep best stars)
          const existing = newProgress[existingIndex];
          if (existing) {
            newProgress[existingIndex] = {
              ...progressEntry,
              starsEarned: Math.max(existing.starsEarned, result.starsEarned) as 1 | 2 | 3,
            };
          }
        } else {
          newProgress.push(progressEntry);
        }

        // Update session stats
        const newStats: SessionStats = {
          ...sessionStats,
          challengesCompleted: sessionStats.challengesCompleted + (existingIndex < 0 ? 1 : 0),
          starsEarned: sessionStats.starsEarned + result.starsEarned,
        };

        set({
          sessionProgress: newProgress,
          sessionStats: newStats,
        });

        // Sync with API
        const response = await state.syncProgress(currentChild.id, result);

        // Handle badge earned
        if (response?.badgeEarned) {
          set({
            earnedBadge: response.badgeEarned,
            showBadgeModal: true,
            currentScreen: "badge-earned",
          });
        }

        // Check if level is complete
        if (response?.levelComplete) {
          set({ isLevelComplete: true });
        }
      },

      nextChallenge: () => {
        const state = get();
        const { currentChallengeIndex, levelChallenges, isLevelComplete } = state;

        // If level is complete, show completion screen
        if (isLevelComplete) {
          set({ currentScreen: "level-complete" });
          return false;
        }

        // Find next uncompleted challenge
        const nextIndex = currentChallengeIndex + 1;

        if (nextIndex >= levelChallenges.length) {
          // All challenges done - check if level is complete
          const progress = state.getLevelProgress();
          if (progress.completed >= progress.total) {
            set({
              isLevelComplete: true,
              currentScreen: "level-complete",
            });
          } else {
            // Loop back to find first uncompleted
            for (let i = 0; i < levelChallenges.length; i++) {
              const challenge = levelChallenges[i];
              if (challenge && !state.isChallengeDone(challenge.id)) {
                set({
                  currentChallengeIndex: i,
                  currentChallenge: challenge,
                });
                return true;
              }
            }
            // If all done, go to level complete
            set({
              isLevelComplete: true,
              currentScreen: "level-complete",
            });
          }
          return false;
        }

        // Move to next challenge
        set({
          currentChallengeIndex: nextIndex,
          currentChallenge: levelChallenges[nextIndex],
        });
        return true;
      },

      skipChallenge: () => {
        const state = get();
        state.nextChallenge();
      },

      // ==================== Session Management ====================

      startSession: () => {
        set({
          sessionStats: {
            sessionStartedAt: new Date().toISOString(),
            timeInSession: 0,
            challengesCompleted: 0,
            starsEarned: 0,
          },
          sessionProgress: [],
        });
      },

      endSession: () => {
        set({
          currentChallenge: null,
          currentLevel: null,
          currentDiscipline: null,
          levelChallenges: [],
          currentChallengeIndex: 0,
          currentScreen: "discipline-select",
          isLevelComplete: false,
        });
      },

      updateSessionTime: () => {
        const state = get();
        const startTime = new Date(state.sessionStats.sessionStartedAt).getTime();
        const now = Date.now();
        const timeInSession = Math.floor((now - startTime) / 1000);

        set({
          sessionStats: {
            ...state.sessionStats,
            timeInSession,
          },
        });
      },

      // ==================== UI State ====================

      setLoading: (loading, message = "") => {
        set({ isLoading: loading, loadingMessage: message });
      },

      setError: (error) => {
        set({ error });
      },

      showBadge: (badge) => {
        set({
          earnedBadge: badge,
          showBadgeModal: true,
        });
      },

      hideBadgeModal: () => {
        const state = get();
        set({
          showBadgeModal: false,
          earnedBadge: null,
        });

        // If level is complete, show level complete screen
        if (state.isLevelComplete) {
          set({ currentScreen: "level-complete" });
        } else {
          // Continue to next challenge
          state.nextChallenge();
        }
      },

      setLevelComplete: (complete) => {
        set({ isLevelComplete: complete });
      },

      setScreen: (screen) => {
        set({ currentScreen: screen });
      },

      // ==================== API Sync ====================

      syncProgress: async (childId, result) => {
        set({ isSyncing: true });

        try {
          const response = await apiPost<RecordProgressResponse>(
            `/api/progress/${childId}/progress`,
            {
              challengeId: result.challengeId,
              starsEarned: result.starsEarned,
              timeSpentSeconds: result.timeSpentSeconds,
              attempts: result.attempts,
            }
          );

          if (response.error) {
            console.error("Failed to sync progress:", response.error);
            return null;
          }

          return response.data || null;
        } catch (error) {
          console.error("Error syncing progress:", error);
          return null;
        } finally {
          set({ isSyncing: false });
        }
      },

      loadProgress: async (childId) => {
        set({ isLoading: true, loadingMessage: "Loading progress..." });

        try {
          interface ProgressApiResponse {
            childId: string;
            disciplines: Array<{
              levels: Array<{
                challenges: Array<{
                  challengeId: string;
                  isCompleted: boolean;
                  completedAt: string;
                  starsEarned: number;
                  attempts: number;
                  timeSpentSeconds: number;
                }>;
              }>;
            }>;
          }

          const response = await apiGet<ProgressApiResponse>(
            `/api/progress/${childId}/progress`
          );

          if (response.error) {
            throw new Error(response.error.message);
          }

          if (!response.data) {
            throw new Error("No data received");
          }

          // Transform API response to progress map
          const progressMap = new Map<string, ChildProgress>();
          for (const discipline of response.data.disciplines) {
            for (const level of discipline.levels) {
              for (const challenge of level.challenges) {
                if (challenge.isCompleted) {
                  progressMap.set(challenge.challengeId, {
                    id: challenge.challengeId,
                    childId,
                    challengeId: challenge.challengeId,
                    completedAt: challenge.completedAt,
                    starsEarned: challenge.starsEarned as 1 | 2 | 3,
                    attempts: challenge.attempts,
                    timeSpentSeconds: challenge.timeSpentSeconds,
                  });
                }
              }
            }
          }

          set({ cachedProgress: progressMap });
        } catch (error) {
          console.error("Error loading progress:", error);
          set({ error: "Failed to load progress" });
        } finally {
          set({ isLoading: false, loadingMessage: "" });
        }
      },

      loadChildStats: async (childId) => {
        try {
          interface StatsApiResponse {
            childId: string;
            stats: {
              totalStars: number;
              totalBadges: number;
              currentStreakDays: number;
              longestStreakDays: number;
              lastPlayedAt: string | null;
              totalTimePlayedSeconds: number;
            };
          }

          const response = await apiGet<StatsApiResponse>(
            `/api/children/${childId}/stats`
          );

          if (response.error) {
            throw new Error(response.error.message);
          }

          if (!response.data) {
            throw new Error("No data received");
          }

          const stats = response.data.stats as {
            totalStars: number;
            totalBadges: number;
            currentStreakDays: number;
            longestStreakDays: number;
            lastPlayedAt: string | null;
            totalTimePlayedSeconds: number;
            starsByDiscipline?: ChildStats["starsByDiscipline"];
            averageStarsPerChallenge?: number;
          };
          set({
            childStats: {
              id: response.data.childId,
              childId: response.data.childId,
              totalStars: stats.totalStars,
              totalBadges: stats.totalBadges,
              currentStreakDays: stats.currentStreakDays,
              longestStreakDays: stats.longestStreakDays,
              lastPlayedAt: stats.lastPlayedAt,
              totalTimePlayedSeconds: stats.totalTimePlayedSeconds,
              starsByDiscipline: stats.starsByDiscipline ?? {
                math: 0,
                spelling: 0,
                patterns: 0,
                typing: 0,
                programming: 0,
              },
              averageStarsPerChallenge: stats.averageStarsPerChallenge ?? 0,
            },
          });
        } catch (error) {
          console.error("Error loading child stats:", error);
        }
      },

      setCachedProgress: (progress) => {
        const progressMap = new Map<string, ChildProgress>();
        for (const p of progress) {
          progressMap.set(p.challengeId, p);
        }
        set({ cachedProgress: progressMap });
      },

      setChildStats: (stats) => {
        set({ childStats: stats });
      },

      // ==================== Helpers ====================

      getProgressForChallenge: (challengeId) => {
        const state = get();

        // Check session progress first (most recent)
        const sessionEntry = state.sessionProgress.find(
          (p) => p.challengeId === challengeId
        );
        if (sessionEntry) {
          return sessionEntry;
        }

        // Check cached progress from API
        return state.cachedProgress.get(challengeId) || null;
      },

      isChallengeDone: (challengeId) => {
        const state = get();
        return (
          state.sessionProgress.some((p) => p.challengeId === challengeId) ||
          state.cachedProgress.has(challengeId)
        );
      },

      getLevelProgress: () => {
        const state = get();
        const { levelChallenges } = state;

        let completed = 0;
        let stars = 0;

        for (const challenge of levelChallenges) {
          const progress = state.getProgressForChallenge(challenge.id);
          if (progress) {
            completed++;
            stars += progress.starsEarned;
          }
        }

        return {
          completed,
          total: levelChallenges.length,
          stars,
        };
      },

      reset: () => {
        set({ ...initialState });
      },
    }),
    {
      name: "jumpbean-game-session",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist session-critical data
        currentChild: state.currentChild,
        currentDiscipline: state.currentDiscipline,
        currentLevel: state.currentLevel,
        currentChallengeIndex: state.currentChallengeIndex,
        sessionProgress: state.sessionProgress,
        sessionStats: state.sessionStats,
        currentScreen: state.currentScreen,
      }),
      // Custom serialization for Map
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<GameState>;
        return {
          ...currentState,
          ...persisted,
          // Ensure cachedProgress is always a Map
          cachedProgress: currentState.cachedProgress,
        };
      },
    }
  )
);

// ==================== Selectors ====================

/**
 * Selector to check if currently in a game session
 */
export const selectIsInSession = (state: GameState) =>
  state.currentChild !== null && state.currentDiscipline !== null;

/**
 * Selector to check if currently playing a challenge
 */
export const selectIsPlayingChallenge = (state: GameState) =>
  state.currentChallenge !== null;

/**
 * Selector to get current discipline slug
 */
export const selectCurrentDisciplineSlug = (state: GameState): DisciplineSlug | null =>
  state.currentDiscipline?.slug as DisciplineSlug | null;

/**
 * Selector to get total session stars
 */
export const selectSessionStars = (state: GameState) =>
  state.sessionStats.starsEarned;

/**
 * Selector to get session duration in minutes
 */
export const selectSessionDurationMinutes = (state: GameState) =>
  Math.floor(state.sessionStats.timeInSession / 60);
